import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Button, Statistic, Modal, message, Space, Row, Col, Divider, Typography } from 'antd';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { PrinterOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { getLastPaymentDate } from '../utils/enrollment';

const { Title, Text } = Typography;

const CourseDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t, i18n } = useTranslation();
    const printRef = useRef();

    const { data, isLoading } = useQuery(['course', id], async () => {
        const res = await api.get(`/courses/${id}`);
        return res.data.data;
    });

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Course-${data?.code || 'Detail'}`,
    });

    const endCourseMutation = useMutation(() => api.put(`/courses/${id}`, { isActive: false, status: 'ended' }), {
        onSuccess: () => {
            queryClient.invalidateQueries(['course', id]);
            message.success(t('courseMarkedAsEnded'));
        },
        onError: () => {
            message.error(t('failedToEndCourse'));
        }
    });

    const reactivateCourseMutation = useMutation(
        async () => {
            // First delete all certificates for this course
            await api.delete(`/certificates/course/${id}`);
            // Reactivate the course and reset enrollments
            return api.put(`/courses/${id}/reactivate`);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['course', id]);
                queryClient.invalidateQueries('certificates');
                message.success(t('courseReactivated') || 'Course reactivated and certificates deleted successfully');
            },
            onError: () => {
                message.error(t('failedToReactivateCourse') || 'Failed to reactivate course');
            }
        }
    );

    if (isLoading) return <div>{t('loading')}</div>;
    if (!data) return <div>{t('courseNotFound')}</div>;
    const course = data;
    const enrolledStudents = course.enrolledStudents || [];

    // Calculate total income by currency
    const totalIncomeUSD = enrolledStudents.reduce((sum, s) => {
        if (s.payment?.currency === 'USD' || !s.payment?.currency) {
            return sum + (s.payment?.totalFee || 0);
        }
        return sum;
    }, 0);

    const totalIncomeIQD = enrolledStudents.reduce((sum, s) => {
        if (s.payment?.currency === 'IQD') {
            return sum + (s.payment?.totalFee || 0);
        }
        return sum;
    }, 0);

    const columns = [
        {
            title: t('student'),
            key: 'student',
            render: (_, r) => (
                <span
                    style={{ color: '#1677ff', cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => r.student?._id && navigate(`/students/${r.student._id}`)}
                >
                    {r.student?.firstName} {r.student?.lastName}
                </span>
            )
        },
        { title: t('studentId'), dataIndex: 'studentId', key: 'studentId' },
        {
            title: t('phone'),
            key: 'phone',
            render: (_, r) => r.student?.phone || '-'
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: s => <Tag color={s === 'active' ? 'green' : s === 'completed' ? 'blue' : 'red'}>{s?.toUpperCase()}</Tag>
        },
        {
            title: t('registrationDate'),
            key: 'registrationDate',
            render: (_, r) => (r.startDate ? new Date(r.startDate).toLocaleDateString() : '-')
        },
        {
            title: t('lastPaymentDate'),
            key: 'lastPaymentDate',
            render: (_, r) => {
                const lastPaymentDate = getLastPaymentDate(r.payment);
                return lastPaymentDate ? lastPaymentDate.toLocaleDateString() : '-';
            }
        },
        {
            title: t('fee'),
            key: 'fee',
            render: (_, r) => {
                const currency = r.payment?.currency || 'USD';
                const symbol = currency === 'IQD' ? 'IQD' : '$';
                return `${symbol}${(r.payment?.totalFee || 0).toLocaleString()}`;
            }
        },
        {
            title: t('paidAmount') || 'Paid',
            key: 'paidAmount',
            render: (_, r) => {
                const currency = r.payment?.currency || 'USD';
                const symbol = currency === 'IQD' ? 'IQD' : '$';
                return <span style={{ color: '#15803d', fontWeight: 600 }}>{symbol}{(r.payment?.paidAmount || 0).toLocaleString()}</span>;
            }
        },
        {
            title: t('remaining') || 'Remaining',
            key: 'remaining',
            render: (_, r) => {
                const currency = r.payment?.currency || 'USD';
                const symbol = currency === 'IQD' ? 'IQD' : '$';
                const remaining = r.payment?.remainingAmount || 0;
                return <span style={{ color: remaining > 0 ? '#dc2626' : '#15803d', fontWeight: 600 }}>{symbol}{remaining.toLocaleString()}</span>;
            }
        },
        {
            title: t('paymentStatus'),
            key: 'paymentStatus',
            render: (_, r) => (
                <Tag color={r.payment?.paymentStatus === 'completed' ? 'green' : 'orange'}>
                    {r.payment?.paymentStatus?.toUpperCase()}
                </Tag>
            )
        },
    ];

    const handleEndCourse = () => {
        Modal.confirm({
            title: t('endCourse') || 'End Course',
            content: t('areYouSureEndCourse') || `Are you sure you want to end "${course.name}"? This will mark the course as completed.`,
            okText: t('yes') || 'Yes',
            okType: 'danger',
            cancelText: t('cancel') || 'Cancel',
            onOk: () => {
                endCourseMutation.mutate();
            },
        });
    };

    const handleReactivateCourse = () => {
        Modal.confirm({
            title: t('reactivateCourse') || 'Reactivate Course',
            content: t('areYouSureReactivateCourse') || `Are you sure you want to reactivate "${course.name}"? This will mark the course as active again.`,
            okText: t('yes') || 'Yes',
            okType: 'primary',
            cancelText: t('cancel') || 'Cancel',
            onOk: () => {
                reactivateCourseMutation.mutate();
            },
        });
    };

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                <div>
                    <Button onClick={() => navigate(-1)}>{t('back')}</Button>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={handlePrint}
                    >
                        {t('print') || 'Print'}
                    </Button>
                    {course.isActive ? (
                        <Button
                            danger
                            onClick={handleEndCourse}
                            loading={endCourseMutation.isLoading}
                        >
                            {t('endCourse') || 'End Course'}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            onClick={handleReactivateCourse}
                            loading={reactivateCourseMutation.isLoading}
                        >
                            {t('reactivateCourse') || 'Reactivate Course'}
                        </Button>
                    )}
                </div>
            </div>

            <Card title={`${t('course')}: ${course.name} (${course.code})`}>
                <p><b>{t('subject')}:</b> {course.subject?.name?.[i18n.language] || course.subject?.name?.en || '-'}</p>
                <p><b>{t('level')}:</b> {course.level || '-'}</p>
                <p><b>{t('status')}:</b> <Tag color={course.isActive ? 'green' : 'red'}>{course.isActive ? t('active').toUpperCase() : t('ended').toUpperCase()}</Tag></p>
                <p>
                    <b>{t('fee')}:</b>
                    {course.fee?.currency === 'IQD' ? 'IQD' : '$'}{(course.fee?.amount || 0).toLocaleString()}
                </p>
                <p>
                    <b>{t('discount')}:</b>
                    {course.discount > 0 ? (
                        <span>
                            <Tag color="green" style={{ marginLeft: '8px' }}>-{course.fee?.currency === 'IQD' ? 'IQD' : '$'}{course.discount.toLocaleString()}</Tag>
                        </span>
                    ) : (
                        ` ${course.fee?.currency === 'IQD' ? 'IQD' : '$'}${(course.fee?.amount || 0).toLocaleString()}`
                    )}
                </p>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    {totalIncomeIQD > 0 && (
                        <Statistic
                            title={t('totalIncome') + ' (IQD)'}
                            value={totalIncomeIQD}
                            prefix="IQD "
                        />
                    )}
                    {totalIncomeUSD > 0 && (
                        <Statistic
                            title={t('totalIncome') + ' (USD)'}
                            value={totalIncomeUSD}
                            prefix="$"
                        />
                    )}
                </div>

                <h3>{t('enrolledStudents')}</h3>
                <Table columns={columns} dataSource={enrolledStudents} rowKey={(_, i) => i} pagination={false} />

            </Card>

            {/* Hidden Printable Section */}
            <div style={{ display: 'none' }}>
                <div ref={printRef} style={{ padding: '20px', backgroundColor: 'white' }}>
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                            {t('course')} {t('details')}
                        </Title>
                        <Text style={{ color: '#666', fontSize: '14px' }}>
                            {new Date().toLocaleDateString()}
                        </Text>
                    </div>

                    <Divider />

                    {/* Course Information Section */}
                    <div style={{ marginBottom: '30px' }}>
                        <Title level={4} style={{ color: '#1890ff' }}>
                            {t('courseInformation')}
                        </Title>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('courseName')}:</Text>
                                    <Text style={{ marginLeft: '8px' }}>{course.name}</Text>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('courseCode')}:</Text>
                                    <Text style={{ marginLeft: '8px' }}>{course.code}</Text>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('subject')}:</Text>
                                    <Text style={{ marginLeft: '8px' }}>
                                        {course.subject?.name?.[i18n.language] || course.subject?.name?.en || '-'}
                                    </Text>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('level')}:</Text>
                                    <Text style={{ marginLeft: '8px' }}>{course.level || '-'}</Text>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('duration')}:</Text>
                                    <Text style={{ marginLeft: '8px' }}>
                                        {course.duration?.value} {course.duration?.unit}
                                    </Text>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('status')}:</Text>
                                    <Tag color={course.isActive ? 'green' : 'red'} style={{ marginLeft: '8px' }}>
                                        {course.isActive ? t('active').toUpperCase() : t('ended').toUpperCase()}
                                    </Tag>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Divider />

                    {/* Pricing Information Section */}
                    <div style={{ marginBottom: '30px' }}>
                        <Title level={4} style={{ color: '#1890ff' }}>
                            {t('pricing')}
                        </Title>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('fee')}:</Text>
                                    <Text style={{ marginLeft: '8px' }}>
                                        {course.fee?.currency === 'IQD' ? 'IQD' : '$'} {(course.fee?.amount || 0).toLocaleString()}
                                    </Text>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ marginBottom: '12px' }}>
                                    <Text strong>{t('discount')}:</Text>
                                    <Text style={{ marginLeft: '8px' }}>
                                        {course.discount > 0 ? (
                                            <span>{course.fee?.currency === 'IQD' ? 'IQD' : '$'} {course.discount.toLocaleString()}</span>
                                        ) : (
                                            '0'
                                        )}
                                    </Text>
                                </div>
                            </Col>
                            {totalIncomeUSD > 0 && (
                                <Col span={12}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <Text strong>{t('totalIncome')} (USD):</Text>
                                        <Text style={{ marginLeft: '8px', color: '#52c41a', fontWeight: 600 }}>
                                            ${totalIncomeUSD.toLocaleString()}
                                        </Text>
                                    </div>
                                </Col>
                            )}
                            {totalIncomeIQD > 0 && (
                                <Col span={12}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <Text strong>{t('totalIncome')} (IQD):</Text>
                                        <Text style={{ marginLeft: '8px', color: '#52c41a', fontWeight: 600 }}>
                                            IQD {totalIncomeIQD.toLocaleString()}
                                        </Text>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>

                    <Divider />

                    {/* Enrolled Students Section */}
                    <div style={{ marginBottom: '20px' }}>
                        <Title level={4} style={{ color: '#1890ff' }}>
                            {t('enrolledStudents')} ({enrolledStudents.length})
                        </Title>
                        <Table
                            columns={columns}
                            dataSource={enrolledStudents}
                            rowKey={(_, i) => i}
                            pagination={false}
                            bordered
                            size="small"
                            style={{ marginTop: '12px' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseDetails;
